/* R12: MTA-only optional two-item action. Empire owns the native hoodie form. */
(() => {
  if (customElements.get('mta-transit-set-addon')) return;

  class MtaTransitSetAddon extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      const dataElement = this.querySelector('[data-mta-addon-data]');
      this.toggle = this.querySelector('[data-mta-addon-toggle]');
      this.options = this.querySelector('[data-mta-addon-options]');
      this.colors = Array.from(this.querySelectorAll('[data-mta-addon-color]'));
      this.size = this.querySelector('[data-mta-addon-size]');
      this.price = this.querySelector('[data-mta-addon-price]');
      this.image = this.querySelector('[data-mta-addon-image]');
      this.imageFrame = this.querySelector('[data-mta-addon-image-frame]');
      this.status = this.querySelector('[data-mta-addon-status]');
      this.error = this.querySelector('[data-mta-addon-error]');
      this.cartLink = this.querySelector('[data-mta-addon-cart-link]');
      this.pairButton = this.querySelector('[data-mta-addon-pair-button]');
      this.hoodie = this.closest('mta-map-hoodie');
      this.form = this.hoodie?.querySelector('[data-product-form]');
      this.selection = this.form?.querySelector('[data-variant-selection]');
      this.master = this.form?.querySelector('[data-variants][name="id"]');
      const sectionData = this.hoodie?.closest('.shopify-section')?.querySelector('[data-section-data]');
      if (!dataElement || !this.toggle || !this.options || !this.colors.length || !this.size || !this.price || !this.image || !this.imageFrame || !this.status || !this.error || !this.cartLink || !this.pairButton || !this.form || !this.selection || !this.master || !sectionData) return;

      try {
        this.variants = JSON.parse(dataElement.textContent).variants;
        this.config = JSON.parse(sectionData.textContent);
      } catch (_) {
        return;
      }
      if (!Array.isArray(this.variants) || !this.variants.length || !Array.isArray(this.config.product?.variants)) return;

      this.lifecycle = new AbortController();
      const signal = this.lifecycle.signal;
      this.pending = false;
      this.locked = false;
      this.toggle.addEventListener('change', () => this.update(), { signal });
      this.colors.forEach(color => color.addEventListener('change', () => this.update(), { signal }));
      this.size.addEventListener('change', () => this.update(), { signal });
      this.pairButton.addEventListener('click', () => this.addPair(), { signal });
      this.hoodie.addEventListener('variant-change', event => {
        if (event.target !== this.selection) return;
        queueMicrotask(() => {
          if (this.lifecycle?.signal.aborted === false) this.update({ preserveStatus: true });
        });
      }, { signal });
      // The MTA adapter suppresses invalid variant events before bubble listeners.
      this.variantObserver = new MutationObserver(() => {
        if (this.lifecycle?.signal.aborted === false) this.update({ preserveStatus: true });
      });
      this.variantObserver.observe(this.selection, { attributes: true, attributeFilter: ['variant'] });
      this.variantObserver.observe(this.master, { attributes: true, attributeFilter: ['disabled'] });
      const quantityControl = this.form.elements.namedItem('quantity');
      quantityControl?.addEventListener('input', () => this.update({ preserveStatus: true }), { signal });
      quantityControl?.addEventListener('change', () => this.update({ preserveStatus: true }), { signal });
      // A second native submit during the pair request would add another hoodie.
      this.form.addEventListener('submit', event => {
        if (!this.pending) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.status.textContent = 'Please wait while the two items are added.';
      }, { capture: true, signal });
      this.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => this.disconnect(), { signal });
      this.update();
    }

    disconnectedCallback() { this.disconnect(); }

    disconnect() {
      this.lifecycle?.abort();
      this.lifecycle = null;
      this.variantObserver?.disconnect();
      this.variantObserver = null;
    }

    selectedTee() {
      const color = this.colors.find(input => input.checked)?.value;
      return this.variants.find(item => item.color === color && item.size === this.size.value && item.available) || null;
    }

    update({ preserveStatus = false } = {}) {
      if (!preserveStatus && !this.pending && !this.locked) this.error.hidden = true;
      const include = this.toggle.checked;
      this.options.hidden = !include;
      this.toggleAttribute('data-mta-addon-expanded', include);
      const color = this.colors.find(input => input.checked)?.value;
      const sizes = new Set(this.variants.filter(item => item.color === color && item.available).map(item => item.size));
      Array.from(this.size.options).forEach(option => { option.disabled = !sizes.has(option.value); });
      if (!sizes.has(this.size.value)) {
        this.size.value = Array.from(this.size.options).find(option => !option.disabled)?.value || '';
      }
      const variant = this.selectedTee();
      this.imageFrame.hidden = !include || !variant;
      this.pairButton.disabled = !include || !variant || !this.selectedHoodie() || this.pending || this.locked;
      if (!variant) {
        this.removeAttribute('data-mta-addon-variant-id');
        if (!preserveStatus && !this.pending) this.status.textContent = include
          ? 'This T-shirt combination is unavailable.' : 'The hoodie Add to Cart buys the hoodie only.';
        return;
      }

      this.price.textContent = variant.price_text;
      if (variant.image_alt) this.image.alt = variant.image_alt;
      if (variant.image_width) this.image.width = variant.image_width;
      if (variant.image_height) this.image.height = variant.image_height;
      // A collapsed add-on has no src/srcset, so its image cannot compete with the hero.
      if (include) {
        if (variant.image_srcset && this.image.srcset !== variant.image_srcset) this.image.srcset = variant.image_srcset;
        if (variant.image_src && this.image.src !== new URL(variant.image_src, document.baseURI).href) this.image.src = variant.image_src;
        this.dataset.mtaAddonVariantId = String(variant.id);
        const label = color === 'Gainsboro' ? 'Gray' : color;
        if (!preserveStatus && !this.pending) this.status.textContent = `T-shirt: ${label} / ${variant.size}, ${variant.price_text}. Use Add hoodie + T-shirt to add both.`;
      } else {
        this.removeAttribute('data-mta-addon-variant-id');
        if (!preserveStatus && !this.pending) this.status.textContent = 'The hoodie Add to Cart buys the hoodie only.';
      }
    }

    selectedHoodie({ report = false, requireAtc = false } = {}) {
      const id = this.selection.getAttribute('variant');
      const variant = this.config.product.variants.find(item => String(item.id) === String(id));
      const quantityControl = this.form.elements.namedItem('quantity');
      const quantity = Number(quantityControl?.value);
      const atc = this.form.querySelector('[data-product-atc]');
      if (!variant?.available || this.hoodie.restorePending || this.master.disabled || String(this.master.value) !== String(id) || (requireAtc && atc?.disabled) || !Number.isSafeInteger(quantity) || quantity < 1 || (report && !this.form.reportValidity())) return null;
      return { id: Number(variant.id), quantity, label: Array.isArray(variant.options) ? variant.options.join(' / ') : '' };
    }

    cartRoutes() {
      const routes = window.Theme?.routes;
      if (!routes?.cart_add_url || !routes?.cart_url) return null;
      const add = new URL(`${routes.cart_add_url}.js`, window.location.origin);
      const cart = new URL(`${routes.cart_url}.js`, window.location.origin);
      if (add.origin !== window.location.origin || cart.origin !== window.location.origin) return null;
      return { add: add.href, cart: cart.href, page: routes.cart_url };
    }

    async readCart(url) {
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Cart read failed (${response.status}).`);
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error('Cart response was incomplete.');
      return cart;
    }

    lineQuantity(cart, id) {
      return cart.items.reduce((sum, line) => sum + (String(line.variant_id ?? line.id) === String(id) ? Number(line.quantity) || 0 : 0), 0);
    }

    showError(message, needsCartReview = false) {
      this.error.textContent = message;
      this.error.hidden = false;
      if (needsCartReview) this.cartLink.hidden = false;
    }

    async addPair() {
      if (this.pending || this.locked || !this.toggle.checked) return;
      const tee = this.selectedTee();
      const hoodie = this.selectedHoodie({ report: true, requireAtc: true });
      if (!tee || !hoodie) {
        this.showError('Choose available hoodie and T-shirt variants and a valid hoodie quantity.');
        return;
      }
      const routes = this.cartRoutes();
      if (!routes) {
        this.showError('The cart is unavailable. Try the hoodie Add to Cart or reload this page.');
        return;
      }

      this.pending = true;
      this.error.hidden = true;
      this.status.textContent = 'Adding hoodie and T-shirt…';
      this.update({ preserveStatus: true });
      let before;
      try {
        before = await this.readCart(routes.cart);
      } catch (_) {
        this.pending = false;
        this.update({ preserveStatus: true });
        this.showError('Could not check the cart. Nothing was submitted; please try again.');
        this.status.textContent = 'The hoodie Add to Cart still buys the hoodie only.';
        return;
      }

      // One Shopify request, independent of the native hoodie form's submit listener.
      const requested = [{ id: hoodie.id, quantity: hoodie.quantity }, { id: Number(tee.id), quantity: 1 }];
      let addResponse;
      let addData;
      let networkFailure = false;
      try {
        addResponse = await fetch(routes.add, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: requested })
        });
        addData = await addResponse.json();
      } catch (_) {
        networkFailure = true;
      }

      let after;
      try {
        after = await this.readCart(routes.cart);
      } catch (_) {
        // A failed cart read cannot prove what the POST did. Never auto-retry.
        this.pending = false;
        this.locked = true;
        this.update({ preserveStatus: true });
        this.showError('The cart result is uncertain. Review the cart before trying again.', true);
        this.status.textContent = 'Cart verification is required.';
        return;
      }

      const hoodieDelta = this.lineQuantity(after, hoodie.id) - this.lineQuantity(before, hoodie.id);
      const teeDelta = this.lineQuantity(after, tee.id) - this.lineQuantity(before, tee.id);
      const exact = hoodieDelta === hoodie.quantity && teeDelta === 1;
      const responseHasBoth = Array.isArray(addData?.items)
        && addData.items.length === 2
        && requested.every(item => addData.items.some(line => String(line.variant_id ?? line.id) === String(item.id)));
      const unchanged = hoodieDelta === 0 && teeDelta === 0;
      if (!unchanged) window.dispatchEvent(new CustomEvent('cartcount:update', { detail: after }));
      this.pending = false;

      if (addResponse?.ok && responseHasBoth && exact) {
        this.update({ preserveStatus: true });
        this.cartLink.hidden = false;
        const hoodieSelection = hoodie.label ? ` (${hoodie.label})` : '';
        const teeColor = tee.color === 'Gainsboro' ? 'Gray' : tee.color;
        this.status.textContent = `Added ${hoodie.quantity} hoodie${hoodie.quantity === 1 ? '' : 's'}${hoodieSelection} and one ${teeColor} / ${tee.size} T-shirt. Review cart.`;
        if (this.config.settings?.cart_redirection) window.location.assign(routes.page);
        return;
      }

      // Shopify does not document atomic rollback for an errored multi-item add.
      // A network failure can also arrive after the server has accepted the POST.
      if (!unchanged || networkFailure || addResponse?.ok) {
        this.locked = true;
        this.showError('The cart result needs review before another two-item attempt.', true);
        this.status.textContent = `Cart change observed: hoodie ${hoodieDelta >= 0 ? '+' : ''}${hoodieDelta}, T-shirt ${teeDelta >= 0 ? '+' : ''}${teeDelta}.`;
      } else {
        const detail = typeof addData?.description === 'string' ? addData.description : 'Shopify could not add this combination.';
        this.showError(detail);
        this.status.textContent = 'No hoodie or T-shirt was added by this attempt.';
      }
      this.update({ preserveStatus: true });
    }
  }

  customElements.define('mta-transit-set-addon', MtaTransitSetAddon);
})();
